import React, { useState, useEffect } from 'react';
import { Stethoscope, Save } from 'lucide-react';
import {
  RCM_SPECIALTY_IDS,
  RCM_SPECIALTY_LABELS,
  allRcmSpecialtiesEnabled,
} from '../../../../../shared/frontend/rcmSpecialties';

import { getSaasApiBaseUrl } from '../../utils/saasApiBaseUrl';

const getSaaSApiBaseUrl = getSaasApiBaseUrl;

function formatApiError(payload) {
  const d = payload?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ');
  }
  return payload?.message || 'Request failed';
}

/**
 * Organization-level supported RCM specialties (source of truth: organizations.settings.rcm).
 * SaaS PATCH syncs to Remittance Advice and other embedded RCM services.
 */
export default function OrganizationSupportedSpecialtiesSection({
  organization,
  onMessage,
  onSaved,
}) {
  const organizationId = organization?.id || organization?.organization_id;
  const [supported, setSupported] = useState([]);
  const [showSidebarShortcuts, setShowSidebarShortcuts] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const rcm = organization?.settings?.rcm;
    if (!rcm) {
      setSupported([]);
      setShowSidebarShortcuts(false);
      return;
    }
    setSupported(Array.isArray(rcm.supported_specialties) ? rcm.supported_specialties : []);
    setShowSidebarShortcuts(rcm.show_specialty_sidebar_shortcuts === true);
  }, [organization]);

  const toggleSpecialty = (specialtyId) => {
    setSupported((prev) => {
      const next = prev.includes(specialtyId)
        ? prev.filter((s) => s !== specialtyId)
        : [...prev, specialtyId];
      if (allRcmSpecialtiesEnabled(next)) {
        setShowSidebarShortcuts(false);
      }
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!organizationId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getSaaSApiBaseUrl();
      const res = await fetch(`${API_BASE_URL}/organizations/${organizationId}/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          rcm: {
            supported_specialties: supported,
            show_specialty_sidebar_shortcuts: showSidebarShortcuts,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || `Request failed (${res.status})`);
      }
      onMessage?.({
        type: 'success',
        text: 'Supported specialties saved. Remittance Advice and embedded services will use these filters.',
      });
      onSaved?.();
    } catch (err) {
      onMessage?.({
        type: 'error',
        text: err.message || 'Failed to save supported specialties.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="mt-8 pt-8 border-t border-gray-200 space-y-4">
      <div className="flex items-center gap-2">
        <Stethoscope className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Supported specialties</h3>
      </div>
      <p className="text-sm text-gray-500 max-w-2xl">
        Select the clinical lines of business this organization bills for (e.g. dental, behavioral health).
        Remittance Advice uses these for ERA filter chips and specialty tagging. Configure once here — not per service.
      </p>

      <div className="space-y-2">
        {RCM_SPECIALTY_IDS.map((specialtyId) => (
          <label
            key={specialtyId}
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={supported.includes(specialtyId)}
              onChange={() => toggleSpecialty(specialtyId)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-900">
              {RCM_SPECIALTY_LABELS[specialtyId] || specialtyId}
            </span>
          </label>
        ))}
      </div>

      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
        <input
          type="checkbox"
          checked={showSidebarShortcuts}
          onChange={(e) => setShowSidebarShortcuts(e.target.checked)}
          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>
          <span className="font-medium text-gray-900 block">Show specialty shortcuts in Remittance sidebar</span>
          <span className="text-sm text-gray-500">
            Off by default when all specialties are enabled — staff can use filter chips on the Remittance list instead.
          </span>
        </span>
      </label>

      <button
        type="submit"
        disabled={saving || !organizationId}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {saving ? 'Saving…' : 'Save supported specialties'}
      </button>
    </form>
  );
}
